import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRouteSnapshot, NavigationExtras } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export interface RedirectionExtras extends NavigationExtras {
  target?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object,
    private router: Router
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** The Window object from Document defaultView */
  get window(): Window | null {
    return this.isBrowser ? this.document.defaultView : null;
  }

  /** Redirects instantly to the external link without the mediation of the router */
  public redirect(url: string, target: string = '_blank'): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        if (!this.window) {
          return resolve(false); // SSR: window not available
        }
        const result = this.window.open(url, target);
        resolve(!!result);
      } catch (e) {
        reject(e);
      }
    });
  }

  /** Returns true if the given url looks external */
  public external(url: string): boolean {
    if (!url) return false;
    const lower = url.trim().toLowerCase();
    // Direct schemes or protocol-relative
    if (/^(https?:|mailto:|tel:|\/\/)/i.test(lower)) return true;
    // Handle encoded absolute URLs like "https:%2F%2F..."
    try {
      const decoded = decodeURIComponent(url);
      if (/^https?:\/\//i.test(decoded)) return true;
    } catch {
      // ignore decode errors
    }
    return false;
  }

  /** Parses the given url for external redirection */
  private externalUrl(url: string, target?: string): string {
    return `redirect?url=${encodeURIComponent(url)}${target ? `&target=${encodeURIComponent(target)}` : ''}`;
  }

  /** Parses the given url for internal routing */
  private internalUrl(url: string): string {
    if (!url) return this.router.url;
    if (url.startsWith('#')) {
      return this.router.url + url;
    }
    if (url.startsWith('.')) {
      return url.replace(/^\./, this.router.url);
    }
    return url;
  }

  /** Navigates to the given url, redirecting when necessary */
  public navigate(url: string, extras?: RedirectionExtras): Promise<boolean> {
    const link = this.external(url) ? this.externalUrl(url, extras?.target) : this.internalUrl(url);
    return this.router.navigateByUrl(link, extras);
  }

  /** Guard method for route-based external redirection */
  canActivate(route: ActivatedRouteSnapshot) {
    const url = route.queryParamMap.get('url');
    if (this.external(url)) {
      const target = route.queryParamMap.get('target');
      return this.redirect(url, target).then(() => false);
    }
    return true;
  }
}
